import { useState } from "react";

import { Box, Button, Chip, Divider, Stack, Typography } from "@mui/material";

import {
  ShareTfcViaEmail,
  TfcBannerBox,
  TfcHistorySection,
  TfcReadOnlyTable,
} from "components/TotalFreightCost";
import { EVOTRUX_BORDER_GREY } from "constants/index";
import { formatCentsWithCurrency } from "utils";

import { TfcRevisionForm } from "./TfcRevisionForm";

import type { EditableLineItem } from "./TfcEditableTable";

type EditMode = Extract<TfcOperation, "AMEND" | "RESUBMIT"> | null;

interface TfcDisplayProps {
  tfcData: Extract<TfcResponse, { exists: true }>;
  shipment: CarrierCondensedShipment;
  onTfcUpdated: () => void;
}

function toEditableLineItems(items: TfcLineItem[]): EditableLineItem[] {
  return items.map((item) => ({
    id: `rev-${item.id}`,
    description: item.description,
    line_total_cents: item.line_total_cents,
  }));
}

export function TfcDisplay(props: TfcDisplayProps) {
  const { tfcData, shipment, onTfcUpdated } = props;
  const { tfc, available_operations: ops } = tfcData;
  const { latest_revision: revision } = tfc;
  const [editMode, setEditMode] = useState<EditMode>(null);

  const shipperUserName = `${shipment.shipment_creator.first_name} ${shipment.shipment_creator.last_name}`;
  const shipperCompanyName = shipment.shipment_company.name;

  const actionByName = revision.action
    ? `${revision.action.created_by.user.first_name} ${revision.action.created_by.user.last_name}`
    : shipperUserName;
  const actionByCompany = revision.action
    ? revision.action.created_by.company.name
    : shipperCompanyName;

  const formattedTotal = formatCentsWithCurrency(tfc.total_cents, tfc.currency);
  const shipperNote = revision.action?.notes;

  if (editMode !== null) {
    return (
      <TfcRevisionForm
        tfcId={tfc.tfc_id}
        operation={editMode}
        initialLineItems={toEditableLineItems(revision.line_items)}
        initialNote={revision.notes ?? ""}
        bookedPriceCents={tfcData.booked_price_cents}
        currency={tfc.currency}
        onCancel={() => setEditMode(null)}
        onSuccess={() => {
          setEditMode(null);
          onTfcUpdated();
        }}
      />
    );
  }

  return (
    <Box>
      {tfc.state === "PENDING_REVIEW" && (
        <TfcBannerBox borderColor="#ED6C02" backgroundColor="#FFF3E0">
          Your freight costs of <strong>{formattedTotal}</strong> have been
          submitted to <strong>{shipperUserName}</strong>
          {" from "}
          <strong>{shipperCompanyName}</strong>
          {
            " for review. You will be notified when they confirm or request changes."
          }
        </TfcBannerBox>
      )}
      {tfc.state === "NEEDS_RESUBMISSION" && (
        <TfcBannerBox borderColor="#D32F2F" backgroundColor="#FFEBEE">
          <strong>{actionByName}</strong>
          {" from "}
          <strong>{actionByCompany}</strong>
          {
            " has requested changes to your submitted freight costs. Please review and resubmit."
          }
        </TfcBannerBox>
      )}
      {tfc.state === "APPROVED" && (
        <TfcBannerBox borderColor="#2E7D32" backgroundColor="#E8F5E9">
          Your total freight costs of <strong>{formattedTotal}</strong> have
          been confirmed by <strong>{actionByName}</strong>
          {" from "}
          <strong>{actionByCompany}</strong>.
          {
            " You can now send a summary of the confirmed charges to your accounting team below."
          }
        </TfcBannerBox>
      )}

      <TfcReadOnlyTable
        lineItems={revision.line_items}
        totalCents={revision.total_cents}
        currency={tfc.currency}
        bookedPriceCents={tfcData.booked_price_cents}
      />

      {tfc.state === "APPROVED" && (
        <ShareTfcViaEmail tfcId={tfc.tfc_id} shipmentId={shipment.info.id} />
      )}

      {(revision.notes ?? shipperNote) && (
        <Box
          sx={{
            backgroundColor: "#FAFAFA",
            border: `1px solid ${EVOTRUX_BORDER_GREY}`,
            borderRadius: "4px",
            p: 2,
            mt: 2,
            mb: 3,
          }}
        >
          {shipperNote && (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "12px", mb: 0.5 }}>
                Note from {actionByName}
                {" ("}
                {actionByCompany}
                ):
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                }}
              >
                {shipperNote}
              </Typography>
            </Box>
          )}
          {revision.notes && shipperNote && (
            <Divider sx={{ borderColor: "#EEEEEE", my: 1.5 }} />
          )}
          {revision.notes && (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "12px", mb: 0.5 }}>
                Note from you:
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                }}
              >
                {revision.notes}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        {ops.includes("AMEND") && (
          <Button
            variant="outlined"
            onClick={() => setEditMode("AMEND")}
            sx={{ px: 3 }}
          >
            EDIT
          </Button>
        )}
        {ops.includes("RESUBMIT") && (
          <Button
            variant="contained"
            onClick={() => setEditMode("RESUBMIT")}
            sx={{ px: 3 }}
          >
            REVISE &amp; RESUBMIT
          </Button>
        )}
        {tfc.state === "APPROVED" && (
          <Chip
            label="CONFIRMED"
            size="small"
            variant="outlined"
            sx={{
              fontSize: "11px",
              fontWeight: 500,
              textTransform: "uppercase",
              borderColor: "#2E7D32",
              color: "#2E7D32",
              borderRadius: "16px",
              height: "24px",
            }}
          />
        )}
      </Stack>

      <TfcHistorySection
        revisions={tfc.revisions}
        currency={tfc.currency}
        bookedPriceCents={tfcData.booked_price_cents}
      />
    </Box>
  );
}
